// src/components/PictureStatusChip.js
import React, { useState } from 'react';
import { Chip, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import { CheckCircle, Cancel, Help, Warning } from '@mui/icons-material';
import { updatePictureStatusNew } from '../api';

const PictureStatusChip = ({ status, pictureId, driverId, onStatusChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setErrorMessage(''); // Clear any previous error
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus) => {
    setIsLoading(true);
    setErrorMessage('');
    
    try {
      // Get the token from sessionStorage (consistent with rest of app)
      const token = sessionStorage.getItem('fleetToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Call the API to update picture status
      await updatePictureStatusNew(token, pictureId, newStatus);

      // If API call successful, update the status
      if (onStatusChange) {
        onStatusChange(pictureId, newStatus);
      }
      
      handleClose();
    } catch (error) {
      console.error('Error updating status:', error);
      setErrorMessage(error.message || 'Failed to update picture status');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 0: // Pending
        return {
          label: 'Pending',
          color: 'warning',
          icon: <Help fontSize="small" />,
        };
      case 20: // Not valid
        return {
          label: 'Not valid',
          color: 'error',
          icon: <Cancel fontSize="small" />,
        };
      case 30: // Valid
        return {
          label: 'Valid',
          color: 'success',
          icon: <CheckCircle fontSize="small" />,
        };
      case 40: // ID ok
        return {
          label: 'ID ok',
          color: 'success',
          icon: <CheckCircle fontSize="small" />,
        };
      case 50: // ID alert
        return {
          label: 'ID alert',
          color: 'error',
          icon: <Warning fontSize="small" />,
        };
      case 60: // ID verification failure
        return {
          label: 'ID failure',
          color: 'warning',
          icon: <Warning fontSize="small" />,
        };
      case 51: // ID alert valid
        return {
          label: 'ID alert valid',
          color: 'success',
          icon: <CheckCircle fontSize="small" />,
        };
      case 52: // ID alert invalid
        return {
          label: 'ID alert invalid',
          color: 'error',
          icon: <Cancel fontSize="small" />,
        };
      case 61: // ID failure valid
        return {
          label: 'ID failure valid',
          color: 'success',
          icon: <CheckCircle fontSize="small" />,
        };
      case 62: // ID failure invalid
        return {
          label: 'ID failure invalid',
          color: 'error',
          icon: <Cancel fontSize="small" />,
        };
      default:
        return {
          label: 'Unknown',
          color: 'default',
          icon: null,
        };
    }
  };

  const statusConfig = getStatusConfig(status);

  // Get allowed status transitions based on current status
  const getMenuItems = () => {
    const allStatuses = [
      { value: 0, label: 'Pending', icon: <Help fontSize="small" /> },
      { value: 20, label: 'Not valid', icon: <Cancel fontSize="small" /> },
      { value: 30, label: 'Valid', icon: <CheckCircle fontSize="small" /> },
      { value: 40, label: 'ID ok', icon: <CheckCircle fontSize="small" /> },
      { value: 50, label: 'ID alert', icon: <Warning fontSize="small" /> },
      { value: 51, label: 'ID alert valid', icon: <CheckCircle fontSize="small" /> },
      { value: 52, label: 'ID alert invalid', icon: <Cancel fontSize="small" /> },
      { value: 60, label: 'ID failure', icon: <Warning fontSize="small" /> },
      { value: 61, label: 'ID failure valid', icon: <CheckCircle fontSize="small" /> },
      { value: 62, label: 'ID failure invalid', icon: <Cancel fontSize="small" /> }
    ];

    // Define allowed transitions based on current status
    let allowedTransitions = [];

    switch (status) {
      case 0: // Pending (Recognition tab)
        allowedTransitions = [20, 30]; // Can go to Not valid or Valid
        break;
      case 20: // Not valid (Recognition tab)
        allowedTransitions = [0, 30]; // Can go to Pending or Valid
        break;
      case 30: // Valid (Recognition tab)
        allowedTransitions = [0, 20]; // Can go to Pending or Not valid
        break;
      case 50: // ID alert (Identity failures tab)
        allowedTransitions = [51, 52]; // Can go to ID alert valid or ID alert invalid
        break;
      case 60: // ID verification failure (Identity failures tab)
        allowedTransitions = [61, 62]; // Can go to ID failure valid or ID failure invalid
        break;
      default:
        allowedTransitions = []; // No transitions allowed for other statuses
        break;
    }

    // Return only allowed transition menu items
    return allStatuses
      .filter(statusOption => allowedTransitions.includes(statusOption.value))
      .map(statusOption => (
        <MenuItem
          key={statusOption.value}
          onClick={() => handleStatusChange(statusOption.value)}
          disabled={isLoading}
        >
          <ListItemIcon>
            {statusOption.icon}
          </ListItemIcon>
          <ListItemText>Mark as {statusOption.label}</ListItemText>
        </MenuItem>
      ));
  };

  // Check if status allows editing based on defined transition rules
  const isReadOnly = () => {
    // If onStatusChange callback is not provided, make it read-only
    if (!onStatusChange) {
      return true;
    }
    // Statuses that allow transitions: 0, 20, 30, 50, 60
    const editableStatuses = [0, 20, 30, 50, 60];
    return !editableStatuses.includes(status);
  };

  const chipElement = (
    <Chip
      label={statusConfig.label}
      color={statusConfig.color}
      icon={statusConfig.icon}
      onClick={isReadOnly() ? undefined : handleClick}
      size="small"
      disabled={isLoading || isReadOnly()}
      sx={{
        height: '18px', // 25% smaller than default small chip (24px -> 18px)
        fontSize: '0.7rem', // Smaller font size
        backgroundColor: status === 50 ? '#f44336' : undefined, // Red background for Alert
        color: status === 50 ? 'white' : undefined, // White text for Alert
        '& .MuiChip-label': {
          px: 0.5,
          py: 0,
          color: status === 50 ? 'white' : undefined, // White text for Alert
        },
        '& .MuiChip-icon': {
          fontSize: '0.8rem', // Smaller icon
          color: status === 50 ? 'white' : undefined, // White icon for Alert
        },
        cursor: (isLoading || isReadOnly()) ? 'default' : 'pointer',
      }}
    />
  );

  return (
    <>
      {errorMessage ? (
        <Tooltip title={errorMessage} arrow placement="top">
          {chipElement}
        </Tooltip>
      ) : (
        chipElement
      )}
      {!isReadOnly() && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          {getMenuItems()}
        </Menu>
      )}
    </>
  );
};

export default PictureStatusChip;
